import React, { useEffect, useState } from 'react';
import { Table, Button, Space, message, Modal } from 'antd';
import { PlusOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { getDriverCoreSkills, upsertDriverCoreSkills, deleteDriverCoreSkills } from '@/service/driverArchive';
import CoreSkillFormModal from './CoreSkillFormModal';

const { confirm } = Modal;

/**
 * 驾驶技能表格属性
 */
interface DrivingSkillsTableProps {
  isViewMode: boolean;
  driverId?: number | string;
}

/**
 * 驾驶技能表格组件
 */
const DrivingSkillsTable: React.FC<DrivingSkillsTableProps> = ({
  isViewMode,
  driverId,
}) => {
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // 加载数据
  const loadData = async () => {
    if (!driverId) {
      console.log('没有司机ID，跳过加载核心技能数据');
      return;
    }
    
    setLoading(true);
    try {
      const response = await getDriverCoreSkills(driverId);
      if (response.data?.success && response.data?.data) {
        setDataSource(response.data.data);
      } else {
        setDataSource([]);
      }
    } catch (error) {
      console.error('加载核心技能数据失败:', error);
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  };

  // 组件创建时加载数据
  useEffect(() => {
    loadData();
  }, [driverId]);

  // 新增按钮点击事件
  const handleAdd = () => {
    setModalMode('add');
    setEditingRecord(null);
    setModalVisible(true);
  };

  // 编辑按钮点击事件
  const handleEdit = (record: any) => {
    setModalMode('edit');
    setEditingRecord(record);
    setModalVisible(true);
  };

  // 删除按钮点击事件
  const handleDelete = (record: any) => {
    confirm({
      title: '确认删除',
      icon: <ExclamationCircleOutlined />,
      content: '确定要删除这条核心技能记录吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await deleteDriverCoreSkills(record.id);
          if (response.data?.success) {
            message.success('删除成功');
            loadData(); // 刷新列表
          } else {
            message.error(response.data?.msg || '删除失败');
          }
        } catch (error) {
          console.error('删除核心技能记录失败:', error);
          message.error('删除失败，请重试');
        }
      },
    });
  };

  // 弹窗取消
  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingRecord(null);
  };

  // 弹窗确认
  const handleModalOk = async (values: any) => {
    if (!driverId) {
      message.error('缺少司机ID');
      return;
    }

    setConfirmLoading(true);
    try {
      const submitData = {
        ...values,
        driverId: driverId,
        // 如果是编辑模式，带上id
        ...(modalMode === 'edit' && editingRecord ? { id: editingRecord.id } : {}),
      };

      const response = await upsertDriverCoreSkills(submitData);
      
      if (response.data?.success) {
        message.success(modalMode === 'add' ? '新增成功' : '编辑成功');
        setModalVisible(false);
        setEditingRecord(null);
        loadData(); // 刷新列表
      } else {
        message.error(response.data?.msg || '操作失败');
      }
    } catch (error) {
      console.error('保存核心技能记录失败:', error);
      message.error('操作失败，请重试');
    } finally {
      setConfirmLoading(false);
    }
  };

  const columns: any[] = [
    {
      title: '燃油类型',
      dataIndex: 'fuelTp',
      key: 'fuelTp',
    },
    {
      title: '驾驶过的车型',
      dataIndex: 'drivenVehModel',
      key: 'drivenVehModel',
    },
    {
      title: '是否有辅助设备驾驶经验',
      dataIndex: 'hasAuxEqptDriveExp',
      key: 'hasAuxEqptDriveExp',
    },
    {
      title: '交通事故出险情况',
      dataIndex: 'trafficAccStat',
      key: 'trafficAccStat',
    },
    {
      title: '是否有长途驾驶经验',
      dataIndex: 'hasLongDistDriveExp',
      key: 'hasLongDistDriveExp',
    },
    {
      title: '是否有长时间驾驶经验',
      dataIndex: 'hasLongTimeDriveExp',
      key: 'hasLongTimeDriveExp',
    },
    {
      title: '是否有夜间工作经验',
      dataIndex: 'hasNightWorkExp',
      key: 'hasNightWorkExp',
    },
    {
      title: '货物运输资质',
      dataIndex: 'cargoTransQual',
      key: 'cargoTransQual',
    },
  ];

  // 仅在编辑模式下添加操作列
  if (!isViewMode) {
    columns.push({
      title: '操作',
      key: 'action',
      width: 150,
      render: (text: any, record: any) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button type="link" size="small" danger onClick={() => handleDelete(record)}>
            删除
          </Button>
        </Space>
      ),
    });
  }

  return (
    <>
      {!isViewMode && (
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新增
          </Button>
        </div>
      )}
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        pagination={false}
        rowKey="id"
        bordered
      />
      <CoreSkillFormModal
        visible={modalVisible}
        mode={modalMode}
        initialValues={editingRecord}
        onCancel={handleModalCancel}
        onOk={handleModalOk}
        confirmLoading={confirmLoading}
      />
    </>
  );
};

export default DrivingSkillsTable;
